import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import { BreakPoint, Styles } from 'src/components/shared/CssValues'
import { Typography } from 'src/components/atoms/Typography'
import {
  features,
  planNames
} from 'src/components/organisms/plan/PlanComparison'

type StyledProps = {
  isEnterprise: boolean
}

type TPlans = {
  name: string
  description: string
  features?: string[]
  otherFeaturesCount?: number
}

const plans: TPlans[] = [
  {
    name: planNames.light,
    description:
      'コミュニケーションの集約統合・双方向化に向けた必須機能から使いたいという企業様におすすめ',
    features: [
      features.group,
      features.pointAndBadge,
      features.customerService
    ],
    otherFeaturesCount: 9
  },
  {
    name: planNames.standard,
    description:
      'さらなる顧客接点の最適化や社内外システムとの連携を通し、より効率的かつ効果的なコミュニティ運用・分析を行いたい企業様におすすめ',
    features: [
      features.dm + '/' + features.qa, 
      features.knowledgeBase + '/' + features.training,
      features.api + '/' + features.sso,
      features.monitoring,
    ],
    otherFeaturesCount: 3
  },
  {
    name: planNames.professional,
    description:
      '専用アプリ等を活用したブランド価値向上に寄与するコミュニティの構築や、より柔軟なコミュニティ運営をしたい企業様におすすめ',
    features: [
      features.originalApp, 
      features.originalDomain,
      features.customizeReport,
      features.moreSupport
    ]
  },
  {
    name: planNames.enterprise,
    description:
      '数万人〜の大規模コミュニティ運営を行いたい企業様におすすめ (ご要望を伺いながら柔軟に設計いたします)',
  }
]

const IconCheckMark: React.VFC = () => (
  <svg
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ height: '0.785714em', width: '1em', marginTop: '.5em' }}
  >
    <path d="M1 5.5625L4.65217 9.5L13 0.5" stroke="black" />
  </svg>
)

export const PlanSummary = () => {
  const data = useStaticQuery(graphql`
    query {
      enterpriseImage: file(relativePath: { eq: "plan/enterprise.svg" }) {
        publicURL
      }
    }
  `)

  return (
    <Container>
      {plans.map((plan, i) => {
        const lowerPlanName = i > 0 ? plans[i - 1].name : null
        const isEnterprise = plan.name === planNames.enterprise
        return (
          <Plan key={plan.name} isEnterprise={isEnterprise}>
            <PlanText isEnterprise={isEnterprise}>
              <PlanName isEnterprise={isEnterprise}>{plan.name}</PlanName>
              <Description isEnterprise={isEnterprise}>
                {plan.description}
              </Description>
              {!isEnterprise && (
                <Features isEnterprise={isEnterprise}>
                  <FeaturesTitle>
                    {lowerPlanName ? `${lowerPlanName}の機能 ＋` : '主な機能'}
                  </FeaturesTitle>
                  <FeaturesList>
                    {plan.features?.map(feature => (
                      <FeatureItem key={feature}>
                        <IconCheckMark />
                        <FeatureName>{feature}</FeatureName>
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                  {plan.otherFeaturesCount && (
                    <OtherFeatures>
                      その他{plan.otherFeaturesCount}個の機能
                    </OtherFeatures>
                  )}
                </Features>
              )}
            </PlanText>
            {isEnterprise && (
              <PlanImage src={data.enterpriseImage.publicURL} alt="" />
            )}
          </Plan>
        )
      })}
    </Container>
  )
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Styles.spacing[8]};
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const Plan = styled.div<StyledProps>`
  border: 1px solid ${Styles.color.primary[400]};
  border-top-width: 11px;
  padding: ${Styles.spacing[8]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    grid-column: ${({ isEnterprise }) => (isEnterprise ? 'span 3' : 'auto')};
    padding: ${({ isEnterprise }) =>
      isEnterprise
        ? `${Styles.spacing[12]} ${Styles.spacing[9]}`
        : Styles.spacing[9]};
    display: ${({ isEnterprise }) => (isEnterprise ? 'flex' : 'block')};
    align-items: center;
    justify-content: center;
  }
`

const PlanText = styled.div<StyledProps>`
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    flex: 1;
    min-width: 0;
    margin: ${({ isEnterprise }) =>
      isEnterprise ? `0 ${Styles.spacing[10]} 0 0` : '0'};
    height: ${({ isEnterprise }) => (isEnterprise ? 'auto' : '100%')};
    max-width: ${({ isEnterprise }) => (isEnterprise ? '35%' : 'none')};
  }
`

const PlanImage = styled.img`
  display: none;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    display: inline;
  }
`

const TextStyles = styled(Typography)`
  line-height: ${Styles.lineHeight[400]};
  letter-spacing: ${Styles.letterSpacing[400]};
  font-size: ${Styles.fontSize[300]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[400]};
  }
`

const PlanName = styled(Typography)<StyledProps>`
  font-weight: ${Styles.fontWeight.bold};
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  color: ${Styles.color.primary[400]};
  font-size: ${Styles.fontSize[500]};
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    min-height: 70px;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[600]};
    min-height: ${({ isEnterprise }) => (!isEnterprise ? '70px' : 'auto')};
  }
`

const Description = styled(TextStyles)<StyledProps>`
  flex: 1;
  min-height: 0;
  margin: ${Styles.spacing[8]} 0;
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    margin: 0 0 ${Styles.spacing[9]} 0;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: ${({ isEnterprise }) =>
      !isEnterprise ? `0 0 ${Styles.spacing[11]}` : `${Styles.spacing[8]} 0`};
  }
`

const Features = styled(TextStyles)<StyledProps>`
  color: ${Styles.color.grayscale[400]};
  font-size: ${Styles.fontSize[200]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[300]};
    min-height: ${({ isEnterprise }) => (!isEnterprise ? '120px' : 'auto')};
  }
`

const FeaturesTitle = styled.div`
  line-height: ${Styles.lineHeight[300]};
  margin: 0 0 ${Styles.spacing[3]} 0;
`

const FeaturesList = styled.div``

const FeatureItem = styled.div`
  margin: ${Styles.spacing[2]} 0;
  display: flex;
  gap: ${Styles.spacing[4]};
`

const FeatureName = styled.div`
  flex: 1;
  min-width: 0;
`

const OtherFeatures = styled(TextStyles)`
  color: ${Styles.color.grayscale[300]};
  font-size: ${Styles.fontSize[100]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[200]};
  }
`
